import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { Lock, Play, Loader, ChevronDown, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react'
import api from '../api'

const QUICK_HOSTS = [
  'google.com',
  'github.com',
  'cloudflare.com',
  'expired.badssl.com',
  'self-signed.badssl.com',
  'wrong.host.badssl.com',
]

/* Map authError → message tiếng Việt cho người dùng phổ thông */
const AUTH_ERR_VI = {
  CERT_HAS_EXPIRED:              'Chứng chỉ đã hết hạn',
  DEPTH_ZERO_SELF_SIGNED_CERT:   'Chứng chỉ tự ký (self-signed)',
  SELF_SIGNED_CERT_IN_CHAIN:     'Có chứng chỉ tự ký trong chain',
  ERR_TLS_CERT_ALTNAME_INVALID:  'Hostname không khớp với chứng chỉ',
  UNABLE_TO_VERIFY_LEAF_SIGNATURE: 'Không xác minh được chữ ký leaf cert',
  UNABLE_TO_GET_ISSUER_CERT_LOCALLY: 'Thiếu issuer cert trong chain',
  CERT_NOT_YET_VALID:            'Chứng chỉ chưa có hiệu lực',
}

function CertCard({ cert, idx, total }) {
  const [expanded, setExpanded] = useState(idx === 0)

  /* Màu trạng thái: đỏ nếu expired, vàng nếu sắp hết hạn (<30 ngày), xanh nếu OK */
  const color = cert.expired
    ? '#f87171'
    : cert.daysLeft < 30 ? '#fbbf24' : '#34d399'
  const bgColor = cert.expired
    ? 'rgba(248,113,113,.07)'
    : cert.daysLeft < 30 ? 'rgba(251,191,36,.07)' : 'rgba(52,211,153,.07)'

  /* Parse SAN: 'DNS:a, DNS:b, IP:1.2.3.4' → mảng đẹp hơn */
  const sanList = (cert.subjectaltname || '')
    .split(',').map(s => s.trim()).filter(Boolean)

  return (
    <div style={{
      padding: 14, borderRadius: 10, border: `1px solid ${color}55`,
      background: bgColor,
    }}>
      <div onClick={() => setExpanded(v => !v)}
        style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          cursor:'pointer', gap:12, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0, flex:1 }}>
          <span style={{
            padding:'3px 9px', borderRadius:6, fontSize:11, fontWeight:700,
            fontFamily:'JetBrains Mono,monospace',
            color, background: color+'22', border:`1px solid ${color}55`, flexShrink:0,
          }}>
            #{idx + 1}/{total} {idx === 0 ? 'LEAF' : idx === total - 1 ? 'ROOT' : 'INTERMEDIATE'}
          </span>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:600, color:'var(--text)',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {cert.subjectCN || '(no CN)'}
            </div>
            <div style={{ fontSize:12, color:'var(--text3)',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              ← {cert.issuerCN || '(no issuer CN)'}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          {cert.selfSigned && (
            <span className="chip chip-orange" style={{ fontSize:11 }}>self-signed</span>
          )}
          {cert.expired ? (
            <span className="chip chip-red" style={{ fontSize:11 }}>
              Hết hạn {Math.abs(cert.daysLeft)} ngày trước
            </span>
          ) : cert.notYetValid ? (
            <span className="chip chip-orange" style={{ fontSize:11 }}>Chưa hiệu lực</span>
          ) : (
            <span className="chip" style={{
              fontSize:11, color, background: color+'22', border:`1px solid ${color}55`,
            }}>
              Còn {cert.daysLeft} ngày
            </span>
          )}
          <ChevronDown size={14} style={{
            color:'var(--text3)', transform: expanded ? 'rotate(180deg)' : 'none',
            transition:'transform .25s',
          }} />
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${color}33`,
          display:'grid', gridTemplateColumns:'1fr', gap:8, fontSize:13 }}>
          <Row label="Subject"     value={formatDN(cert.subject)} mono />
          <Row label="Issuer"      value={formatDN(cert.issuer)}  mono />
          <Row label="Valid From"  value={fmtDate(cert.validFrom)} />
          <Row label="Valid To"    value={fmtDate(cert.validTo)} />
          <Row label="Key"         value={cert.keyType} />
          <Row label="Serial"      value={cert.serialNumber} mono />
          <Row label="SHA-256"     value={cert.fingerprintSHA256} mono small />
          {sanList.length > 0 && (
            <Row label={`SAN (${sanList.length})`}
              value={
                <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                  {sanList.slice(0, 30).map((s, i) => (
                    <span key={i} className="chip chip-blue"
                      style={{ fontSize:11, fontFamily:'JetBrains Mono,monospace' }}>
                      {s}
                    </span>
                  ))}
                  {sanList.length > 30 && (
                    <span style={{ fontSize:12, color:'var(--text3)' }}>
                      +{sanList.length - 30} nữa
                    </span>
                  )}
                </div>
              }
            />
          )}
        </div>
      )}
    </div>
  )
}

function Row({ label, value, mono, small }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'110px 1fr', gap:8 }}>
      <span style={{ color:'var(--text3)', fontSize:12 }}>{label}</span>
      <span style={{
        color:'var(--text2)',
        fontFamily: mono ? 'JetBrains Mono,monospace' : 'inherit',
        fontSize: small ? 11 : 13,
        wordBreak:'break-all',
      }}>{value || '—'}</span>
    </div>
  )
}

function formatDN(dn) {
  if (!dn || typeof dn !== 'object') return ''
  /* DN có dạng { CN: 'foo', O: 'bar', ... } */
  return Object.entries(dn).map(([k, v]) => `${k}=${v}`).join(', ')
}

function fmtDate(s) {
  if (!s) return ''
  try { return new Date(s).toLocaleString('vi-VN') + ` (${s})` }
  catch { return s }
}

export default function SslChecker() {
  const [host, setHost]         = useState('')
  const [port, setPort]         = useState('443')
  const [servername, setSname]  = useState('')
  const [result, setResult]     = useState(null)
  const [loading, setLoading]   = useState(false)

  const check = async () => {
    const h = host.trim()
    const p = parseInt(port)
    if (!h)              return toast.error('Vui lòng nhập host')
    if (!p || p < 1 || p > 65535)
                         return toast.error('Port không hợp lệ (1-65535)')
    setLoading(true)
    setResult(null)
    try {
      const res = await api.post('/ssl-check', {
        host: h, port: p, servername: servername.trim() || undefined,
      })
      setResult(res.data)
      if (res.data.error) toast.error(res.data.error)
      else if (res.data.authorized) toast.success('Chứng chỉ hợp lệ')
      else toast(res.data.authError ? AUTH_ERR_VI[res.data.authError] || res.data.authError : 'Chứng chỉ có vấn đề', { icon: '⚠️' })
    } catch (e) {
      toast.error('Lỗi: ' + (e.response?.data?.error || e.message))
    } finally { setLoading(false) }
  }

  /* Overall status banner */
  const banner = result && !result.error && (
    result.authorized
      ? { color:'#34d399', bg:'rgba(52,211,153,.08)', icon:CheckCircle2, label:'Chứng chỉ hợp lệ và tin cậy' }
      : result.chain?.[0]?.expired
        ? { color:'#f87171', bg:'rgba(248,113,113,.08)', icon:AlertTriangle, label:'Chứng chỉ ĐÃ HẾT HẠN' }
        : { color:'#fbbf24', bg:'rgba(251,191,36,.08)', icon:ShieldAlert,
            label: AUTH_ERR_VI[result.authError] || result.authError || 'Cảnh báo về chứng chỉ' }
  )

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Title */}
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:40, height:40, borderRadius:10, flexShrink:0,
          background:'linear-gradient(135deg,#10b981,#0d9488)',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 0 18px rgba(16,185,129,.35)' }}>
          <Lock size={20} color="#fff" />
        </div>
        <div>
          <h1 style={{ fontSize:18, fontWeight:700 }}>SSL / TLS Certificate Inspector</h1>
          <p style={{ fontSize:13, color:'var(--text3)' }}>
            Kiểm tra chứng chỉ HTTPS: ngày hết hạn, issuer, SAN, cipher, full chain
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="grad-border p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>
              Host
            </label>
            <input className="inp" placeholder="example.com  hoặc  192.168.1.1"
              value={host} onChange={e => setHost(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && check()} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>
              Port
            </label>
            <input className="inp" type="number" min="1" max="65535"
              value={port} onChange={e => setPort(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && check()} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>
              SNI / Servername <span style={{ color:'var(--text3)', fontWeight:400 }}>(tùy chọn — mặc định = host)</span>
            </label>
            <input className="inp" placeholder="vd: vhost.example.com"
              value={servername} onChange={e => setSname(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && check()} />
          </div>
          <div style={{ display:'flex', alignItems:'flex-end' }}>
            <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }}
              onClick={check} disabled={loading}>
              {loading
                ? <><Loader size={14} className="animate-spin" /> Đang kiểm tra...</>
                : <><Play size={14} /> Kiểm tra SSL</>}
            </button>
          </div>
        </div>

        {/* Quick hosts */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {QUICK_HOSTS.map(h => (
            <button key={h} onClick={() => { setHost(h); setPort('443'); setSname('') }}
              style={{ fontSize:12, color:'var(--text3)', background:'var(--panel)',
                border:'1px solid var(--border)', borderRadius:6, padding:'3px 10px',
                cursor:'pointer', transition:'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent)' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text3)' }}>
              {h}
            </button>
          ))}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="fade-in space-y-4">
          {result.error ? (
            <div className="grad-border p-5" style={{
              borderColor:'rgba(248,113,113,.4)', background:'rgba(248,113,113,.05)',
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <AlertTriangle size={20} color="#f87171" />
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:'var(--danger)' }}>
                    Không kết nối được
                  </div>
                  <div style={{ fontSize:13, color:'var(--text2)', marginTop:4, fontFamily:'JetBrains Mono,monospace' }}>
                    {result.error}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Banner */}
              {banner && (
                <div className="grad-border p-4" style={{
                  borderColor: banner.color+'55', background: banner.bg,
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                    <banner.icon size={22} color={banner.color} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:15, fontWeight:700, color: banner.color }}>
                        {banner.label}
                      </div>
                      <div style={{ fontSize:12, color:'var(--text3)', marginTop:2,
                        fontFamily:'JetBrains Mono,monospace' }}>
                        {result.host}:{result.port}
                        {result.servername !== result.host && ` (SNI: ${result.servername})`}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      <span className="chip chip-blue">{result.protocol}</span>
                      <span className="chip chip-blue">{result.latency}ms</span>
                      {result.alpnProtocol && <span className="chip chip-green">ALPN: {result.alpnProtocol}</span>}
                    </div>
                  </div>
                  {result.cipher && (
                    <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${banner.color}22`,
                      fontSize:12, color:'var(--text3)', fontFamily:'JetBrains Mono,monospace' }}>
                      Cipher: <span style={{ color:'var(--text2)' }}>
                        {result.cipher.name} ({result.cipher.version})
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Chain */}
              <div className="grad-border p-5 space-y-3">
                <h3 className="font-display" style={{ fontSize:15 }}>
                  📜 Certificate Chain — {result.chainLength} cert
                </h3>
                <p style={{ fontSize:12, color:'var(--text3)' }}>
                  Click vào mỗi cert để xem chi tiết
                </p>
                {result.chain.map((c, i) => (
                  <CertCard key={i} cert={c} idx={i} total={result.chain.length} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
